/**
 * 剧本监测 · 素材列表
 */

import { useCallback, useEffect, useState } from "react";
import {
  App,
  Button,
  Card,
  Empty,
  Image,
  Input,
  Popconfirm,
  Segmented,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  Upload,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { DeleteOutlined, ReloadOutlined, UploadOutlined } from "../components/icons";
import { trpc } from "../trpc";
import {
  DISPATCH_SCRIPT_MATERIAL_TYPE_LABELS,
  type DispatchScriptMaterialType,
} from "../../shared/dispatchScriptMaterial";

const { Text, Title, Paragraph } = Typography;

type MaterialRecord = {
  id: string;
  type: DispatchScriptMaterialType;
  name: string;
  originalFileName: string;
  ossUrl: string;
  mediaProxyUrl: string;
  mimeType: string;
  sizeBytes: number;
  dHash: string;
  dHashStatus: string;
  dHashError: string;
  source: string;
  sourceMediaMd5: string;
  remark: string;
  createdAt: string | null;
};

function formatBytes(size: number) {
  if (!Number.isFinite(size) || size <= 0) return "—";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function materialPreviewUrl(item: MaterialRecord) {
  return item.ossUrl || item.mediaProxyUrl || "";
}

function MaterialRemarkCell({
  row,
  onSaved,
}: {
  row: MaterialRecord;
  onSaved: () => void;
}) {
  const { message } = App.useApp();
  const [value, setValue] = useState(row.remark || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(row.remark || "");
  }, [row.id, row.remark]);

  const save = async (next: string) => {
    const trimmed = next.trim();
    if (trimmed === (row.remark || "")) {
      setValue(trimmed);
      return;
    }
    setSaving(true);
    try {
      await trpc.dispatchScript.materialUpdate.mutate({ id: row.id, remark: trimmed });
      message.success("备注已保存");
      onSaved();
    } catch (error: any) {
      message.error(error?.message || "保存备注失败");
      setValue(row.remark || "");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Input.TextArea
      value={value}
      autoSize={{ minRows: 1, maxRows: 3 }}
      placeholder="添加备注"
      disabled={saving}
      onChange={(event) => setValue(event.target.value)}
      onBlur={() => void save(value)}
      onPressEnter={(event) => {
        event.preventDefault();
        (event.target as HTMLTextAreaElement).blur();
      }}
    />
  );
}

export function DispatchScriptMaterialPage() {
  const { message } = App.useApp();
  const [typeFilter, setTypeFilter] = useState<DispatchScriptMaterialType | "all">("all");
  const [keyword, setKeyword] = useState("");
  const [items, setItems] = useState<MaterialRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(24);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await trpc.dispatchScript.materialList.query({
        type: typeFilter === "all" ? undefined : typeFilter,
        keyword: keyword.trim() || undefined,
        page,
        pageSize,
      });
      setItems(result.items as MaterialRecord[]);
      setTotal(result.total);
    } catch (error: any) {
      message.error(error?.message || "加载素材失败");
    } finally {
      setLoading(false);
    }
  }, [keyword, message, page, pageSize, typeFilter]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const handleUpload = async (file: File, type: DispatchScriptMaterialType) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("type", type);
      formData.append("name", file.name);
      const response = await fetch("/api/upload/script-material", {
        method: "POST",
        body: formData,
        credentials: "include",
      });
      const payload = (await response.json().catch(() => ({}))) as {
        error?: string;
        material?: MaterialRecord;
      };
      if (!response.ok) {
        throw new Error(payload.error || `上传失败 (HTTP ${response.status})`);
      }
      message.success(
        payload.material?.dHashStatus === "failed"
          ? `已上传，但 dHash 计算失败：${payload.material.dHashError || "未知错误"}`
          : "素材已上传"
      );
      setPage(1);
      await loadData();
    } catch (error: any) {
      message.error(error?.message || "上传失败");
    } finally {
      setUploading(false);
    }
    return false;
  };

  const handleDelete = async (id: string) => {
    try {
      await trpc.dispatchScript.materialDelete.mutate({ id });
      message.success("已删除");
      await loadData();
    } catch (error: any) {
      message.error(error?.message || "删除失败");
    }
  };

  const columns: ColumnsType<MaterialRecord> = [
    {
      title: "预览",
      width: 100,
      render: (_: unknown, row) => {
        if (row.type === "video") {
          return <Tag>视频</Tag>;
        }
        const url = materialPreviewUrl(row);
        return url ? (
          <Image
            src={url}
            alt={row.name}
            width={64}
            height={64}
            style={{ objectFit: "contain", background: "#f5f5f5" }}
            fallback="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='64' height='64'%3E%3Crect width='100%25' height='100%25' fill='%23eee'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23999' font-size='10'%3E无图%3C/text%3E%3C/svg%3E"
          />
        ) : (
          <Text type="secondary">—</Text>
        );
      },
    },
    {
      title: "名称",
      dataIndex: "name",
      ellipsis: true,
    },
    {
      title: "类型",
      width: 90,
      render: (_: unknown, row) => (
        <Tag>{DISPATCH_SCRIPT_MATERIAL_TYPE_LABELS[row.type] || row.type}</Tag>
      ),
    },
    {
      title: "dHash",
      width: 220,
      render: (_: unknown, row) => {
        if (row.type === "video" || row.dHashStatus === "skipped") {
          return <Text type="secondary">视频不处理</Text>;
        }
        if (row.dHashStatus === "failed") {
          return (
            <Text type="danger" title={row.dHashError}>
              计算失败
            </Text>
          );
        }
        if (!row.dHash) return <Text type="secondary">—</Text>;
        return (
          <Text code copyable={{ text: row.dHash }}>
            {row.dHash}
          </Text>
        );
      },
    },
    {
      title: "大小",
      width: 100,
      render: (_: unknown, row) => formatBytes(row.sizeBytes),
    },
    {
      title: "来源",
      width: 90,
      render: (_: unknown, row) => (
        <Tag>{row.source === "archive" ? "存档" : "上传"}</Tag>
      ),
    },
    {
      title: "备注",
      width: 200,
      render: (_: unknown, row) => (
        <MaterialRemarkCell row={row} onSaved={() => void loadData()} />
      ),
    },
    {
      title: "创建时间",
      width: 170,
      render: (_: unknown, row) =>
        row.createdAt
          ? new Date(row.createdAt).toLocaleString("zh-CN", { hour12: false })
          : "—",
    },
    {
      title: "操作",
      width: 90,
      render: (_: unknown, row) => (
        <Popconfirm title="确认删除该素材？" onConfirm={() => handleDelete(row.id)}>
          <Button type="link" danger size="small" icon={<DeleteOutlined />}>
            删除
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <Space orientation="vertical" size={16} style={{ width: "100%" }}>
      <div className="page-title-row">
        <div>
          <Title level={3} style={{ marginBottom: 4 }}>
            素材列表
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            维护表情 / 图片 / 视频素材；表情与图片上传后自动计算首帧 dHash，供预定剧本关联匹配。
          </Paragraph>
        </div>
        <Space wrap>
          <Button icon={<ReloadOutlined />} loading={loading} onClick={() => void loadData()}>
            刷新
          </Button>
          <Upload
            accept="image/png,image/jpeg,image/gif,image/webp"
            showUploadList={false}
            beforeUpload={(file) => {
              void handleUpload(file, "sticker");
              return false;
            }}
          >
            <Button icon={<UploadOutlined />} loading={uploading}>
              上传表情
            </Button>
          </Upload>
          <Upload
            accept="image/png,image/jpeg,image/gif,image/webp"
            showUploadList={false}
            beforeUpload={(file) => {
              void handleUpload(file, "image");
              return false;
            }}
          >
            <Button icon={<UploadOutlined />} loading={uploading}>
              上传图片
            </Button>
          </Upload>
          <Upload
            accept="video/mp4,video/quicktime,video/webm,video/*"
            showUploadList={false}
            beforeUpload={(file) => {
              void handleUpload(file, "video");
              return false;
            }}
          >
            <Button icon={<UploadOutlined />} loading={uploading}>
              上传视频
            </Button>
          </Upload>
        </Space>
      </div>

      <Card variant="borderless" className="shadow-sm">
        <Space orientation="vertical" size={12} style={{ width: "100%" }}>
          <Space wrap>
            <Segmented
              value={typeFilter}
              onChange={(value) => {
                setTypeFilter(value as DispatchScriptMaterialType | "all");
                setPage(1);
              }}
              options={[
                { label: "全部", value: "all" },
                { label: "表情", value: "sticker" },
                { label: "图片", value: "image" },
                { label: "视频", value: "video" },
              ]}
            />
            <Input.Search
              allowClear
              placeholder="搜索名称 / 备注"
              style={{ width: 220 }}
              onSearch={(value) => {
                setKeyword(value);
                setPage(1);
              }}
              onChange={(event) => {
                if (!event.target.value && keyword) {
                  setKeyword("");
                  setPage(1);
                }
              }}
            />
          </Space>
          <Spin spinning={loading}>
            {!items.length ? (
              <Empty description="暂无素材" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <Table
                rowKey="id"
                columns={columns}
                dataSource={items}
                scroll={{ x: 1200 }}
                pagination={{
                  current: page,
                  pageSize,
                  total,
                  showSizeChanger: true,
                  onChange: (nextPage, nextSize) => {
                    setPage(nextPage);
                    setPageSize(nextSize);
                  },
                }}
              />
            )}
          </Spin>
        </Space>
      </Card>
    </Space>
  );
}
